import styled from 'styled-components'
import { useEffect, useRef, useState } from 'react'
import { colors } from '~/assets/styleConstants'
import { TextButton } from './IconTextButton'
import { TextInput } from './Input'

const EditableTextInput = styled(TextInput)`
    margin-right: 8px;
    flex-grow: 1;
    background-color: inherit;
    text-overflow: ellipsis;
    text-align: ${(props: { alignCentered: boolean | undefined }) =>
        props.alignCentered ? 'center' : 'inherit'};
    font-size: inherit;
    max-width: 100%;
    width: ${(props) => props.width}px;
`

const Wrapper = styled.div`
    display: flex;
    flex-direction: row;

    justify-content: space-between;
    align-items: center;
    border-bottom: ${(props: { hasBottomBorder: boolean | undefined }) =>
        props.hasBottomBorder ? `solid 1px ${colors.black}` : ''};
`

const Placeholder = styled.div`
    width: ${(props: { size: number }) => props.size}px;
    height: ${(props: { size: number }) => props.size}px;
    flex-shrink: 0;
    margin-right: 8px;
`
const Button = styled.div`
    cursor: pointer;
`

export type InputFieldProps = {
    initialValue?: string
    onBlur?: (value: string) => void
    placeholder?: string
    onChange?: (value: string) => void
    onFocus?: () => void
    maxLength?: number
    shouldClearInput?: boolean
    alignCentered?: boolean
    focusOnMount?: boolean
}

type TextFieldWithButtonProps = InputFieldProps & {
    button: React.ReactNode
    placeholderSize?: number
    onButtonClick?: (text?: string) => void
    hasBottomBorder?: boolean
}

export function TextFieldWithButton({
    initialValue,
    onBlur,
    placeholder,
    onChange,
    onFocus,
    maxLength,
    shouldClearInput,
    alignCentered,
    focusOnMount,
    button,
    placeholderSize,
    onButtonClick,
    hasBottomBorder,
}: TextFieldWithButtonProps) {
    const [editedText, setEditedText] = useState<string>(initialValue ?? '')
    const inputRef = useRef<HTMLInputElement>(null)

    const handleFocus = () => {
        if (onFocus) {
            onFocus()
        }
        if (shouldClearInput) {
            setEditedText('')
        }
    }

    const handleTextChanged = (evt: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            onChange(evt.target.value)
        }
        setEditedText(evt.target.value)
    }

    const handleInputBlur = () => {
        if (onBlur) {
            onBlur(editedText)
        }
    }

    const handleButtonClick = () => {
        if (onButtonClick) {
            onButtonClick(editedText)
        }
    }

    const refocusInput = () => {
        if (inputRef.current) {
            inputRef.current.focus()
            inputRef.current.selectionStart = inputRef.current.selectionEnd =
                inputRef.current.value.length
        }
    }

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (inputRef.current && e.key === 'Enter') {
            inputRef.current.blur()
        }
    }

    useEffect(() => {
        if (focusOnMount && inputRef.current) {
            inputRef.current.focus()
        }
    }, [focusOnMount, inputRef])

    return (
        <Wrapper hasBottomBorder={hasBottomBorder}>
            {alignCentered && <Placeholder size={placeholderSize || 0} />}
            <EditableTextInput
                data-cy="editableTextInput"
                ref={inputRef}
                value={editedText}
                placeholder={placeholder}
                onChange={handleTextChanged}
                onBlur={handleInputBlur}
                maxLength={maxLength}
                onFocus={handleFocus}
                onKeyDown={handleKeyPress}
                width={(editedText.length + 1) * 11}
                alignCentered={alignCentered}
            />
            <Button
                onClick={onButtonClick ? handleButtonClick : refocusInput}
                onKeyUp={onButtonClick ? handleButtonClick : refocusInput}
                role="button"
                tabIndex={0}>
                {button}
            </Button>
        </Wrapper>
    )
}

type TextWithCaptureBtnProps = {
    buttonText: string
    onButtonClick: (text?: string) => void
    fontSize?: string
}
export const TextFieldWithCaptureButton: React.FunctionComponent<
    InputFieldProps & TextWithCaptureBtnProps
> = (props) => (
    <TextFieldWithButton
        button={
            <TextButton
                text={props.buttonText}
                color={colors.captureBlue}
                fontSize={props.fontSize}
            />
        }
        {...props}
    />
)
